import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';

// Icon names that AdminLayout knows how to render (see MENU_ICONS there).
const ICON_OPTIONS = [
  'Dashboard', 'Hotel', 'Home', 'MeetingRoom', 'AddBox',
  'Inventory', 'Work', 'Settings', 'Description', 'MonetizationOn',
];

export default function MenuMaster() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const empty = {
    id: 0,
    menuKey: '',
    text: '',
    path: '',
    icon: 'Dashboard',
    parentId: '',
    sortOrder: 100,
    masterOnly: false,
    isActive: true,
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/menu/all/${company.id}`, { headers: authHeaders })
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (m) => {
    setForm({
      id: m.id,
      menuKey: m.menuKey || '',
      text: m.text || '',
      path: m.path || '',
      icon: m.icon || 'Dashboard',
      parentId: m.parentId || '',
      sortOrder: m.sortOrder ?? 100,
      masterOnly: !!m.masterOnly,
      isActive: !!m.isActive,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.menuKey.trim()) return toast.error('Menu Key required');
    if (!form.text.trim()) return toast.error('Label (Text) required');
    setSaving(true);
    const payload = {
      menuKey: form.menuKey.trim(),
      text: form.text.trim(),
      path: form.path.trim(),
      icon: form.icon,
      parentId: form.parentId ? Number(form.parentId) : null,
      sortOrder: Number(form.sortOrder) || 100,
      masterOnly: form.masterOnly,
      isActive: form.isActive,
      companyId: null, // global by default
    };
    try {
      const url =
        form.id > 0
          ? `${config.API_BASE_URL}/menu/${form.id}`
          : `${config.API_BASE_URL}/menu`;
      const res = await fetch(url, {
        method: form.id > 0 ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        toast.error(body?.error || 'Failed to save menu');
        return;
      }
      toast.success(form.id > 0 ? 'Menu updated' : 'Menu created');
      setOpen(false);
      load();
    } catch {
      toast.error('Failed to save menu');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (m) => {
    try {
      await fetch(`${config.API_BASE_URL}/menu/${m.id}/toggle`, {
        method: 'POST',
        headers: authHeaders,
      });
      load();
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete menu "${m.text}"?`)) return;
    try {
      await fetch(`${config.API_BASE_URL}/menu/${m.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      toast.success('Menu deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const parentOptions = rows.filter((m) => !m.parentId);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <ToastContainer position='top-right' autoClose={2500} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}>
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 800, color: '#0F172A' }}>
            🧭 Menu Management
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748B' }}>
            Add new sidebar menus from here — no code change needed. (Existing
            built-in menus are managed by the app and not listed here.)
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          New Menu
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Menu Key</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Path</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Parent</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align='center' sx={{ py: 4, color: '#94A3B8' }}>
                  No DB menus yet. Click “New Menu” to add one.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((m) => {
                const parent = rows.find((p) => p.id === m.parentId);
                return (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      {m.text}
                      {m.masterOnly && (
                        <Chip
                          label='Master'
                          size='small'
                          sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{m.menuKey}</TableCell>
                    <TableCell>{m.path}</TableCell>
                    <TableCell>{parent?.text || '—'}</TableCell>
                    <TableCell>{m.sortOrder}</TableCell>
                    <TableCell>
                      <Chip
                        label={m.isActive ? 'Active' : 'Inactive'}
                        size='small'
                        color={m.isActive ? 'success' : 'default'}
                        onClick={() => toggle(m)}
                        sx={{ cursor: 'pointer', fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align='right'>
                      <Tooltip title='Edit'>
                        <IconButton size='small' onClick={() => openEdit(m)}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Delete'>
                        <IconButton size='small' color='error' onClick={() => remove(m)}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {form.id > 0 ? 'Edit Menu' : 'New Menu'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label='Label (sidebar text)'
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label='Menu Key (permission key)'
            value={form.menuKey}
            onChange={(e) => setForm({ ...form, menuKey: e.target.value })}
            helperText='Must match the UserMenuPermissions grant for visibility.'
            fullWidth
          />
          <TextField
            label='Route Path (e.g. /admin/MyPage)'
            value={form.path}
            onChange={(e) => setForm({ ...form, path: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id='icon-lbl'>Icon</InputLabel>
            <Select
              labelId='icon-lbl'
              label='Icon'
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}>
              {ICON_OPTIONS.map((i) => (
                <MenuItem key={i} value={i}>{i}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id='parent-lbl'>Parent (optional)</InputLabel>
            <Select
              labelId='parent-lbl'
              label='Parent (optional)'
              value={form.parentId}
              onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
              <MenuItem value=''>— None (top-level) —</MenuItem>
              {parentOptions
                .filter((p) => p.id !== form.id)
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.text}</MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label='Sort Order'
            type='number'
            value={form.sortOrder}
            onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.masterOnly}
                onChange={(e) => setForm({ ...form, masterOnly: e.target.checked })}
              />
            }
            label='Master-admin only'
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            }
            label='Active'
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} color='inherit'>Cancel</Button>
          <Button onClick={submit} variant='contained' disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
